/**
 * FoodSummary Component
 * Shared component for displaying food information with portion and nutrition data
 */

import React, { useState } from "react";
import { OutgoingSearchResult } from "../types";
import {
    getPortionDisplayText,
    getRoundedMacrosSummary,
    getTotalWeightInGrams,
} from "../utils/nutritionCalculations";

export type FoodSummaryVariant = "compact" | "detailed" | "card";

interface FoodSummaryProps {
    food: OutgoingSearchResult;
    quantity?: number;
    variant?: FoodSummaryVariant;
    onClick?: () => void;
    showAddButton?: boolean;
    onAdd?: () => void;
    className?: string;
    style?: React.CSSProperties;
}

export const FoodSummary: React.FC<FoodSummaryProps> = ({
    food,
    quantity = 1,
    variant = "compact",
    onClick,
    showAddButton = false,
    onAdd,
    className,
    style,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    const portionText = getPortionDisplayText(food, quantity);
    const macrosText = getRoundedMacrosSummary(food.nutritionPreview);

    // Base styles
    const baseStyle: React.CSSProperties = {
        padding: variant === "compact" ? "8px 12px" : "12px 16px",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#e2e8f0",
        borderRadius: variant === "card" ? "8px" : "6px",
        backgroundColor: "#ffffff",
        transition: "all 0.2s",
        ...style,
    };

    const hoverStyle: React.CSSProperties = {
        ...baseStyle,
        backgroundColor: "#f7fafc",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "#3182ce",
        cursor: onClick ? "pointer" : "default",
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: "600",
        fontSize: variant === "compact" ? "13px" : "14px",
        color: "#1a202c",
        marginBottom: "2px",
    };

    const brandStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "11px" : "12px",
        color: "#718096",
        marginBottom: "4px",
    };

    const portionStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "11px" : "12px",
        color: "#2b6cb0",
        fontWeight: "500",
        marginBottom: "2px",
    };

    const macrosStyle: React.CSSProperties = {
        fontSize: variant === "compact" ? "11px" : "12px",
        color: "#4a5568",
    };

    const cardContainerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    };

    const infoStyle: React.CSSProperties = {
        flex: 1,
    };

    const addButtonStyle: React.CSSProperties = {
        padding: "6px 12px",
        backgroundColor: "#48bb78",
        color: "#ffffff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: "500",
        marginLeft: "12px",
        transition: "background-color 0.2s",
    };

    const renderContent = () => {
        if (variant === "compact") {
            const weightInfo = food.nutritionPreview?.portion 
                ? getTotalWeightInGrams(food.nutritionPreview.portion, quantity)
                : null;
                
            return (
                <>
                    <div style={nameStyle}>{food.displayName}</div>
                    {food.brandName && (
                        <div style={brandStyle}>{food.brandName}</div>
                    )}
                    <div style={portionStyle}>
                        {portionText}
                        {weightInfo && weightInfo.displayText && (
                            <span style={{ 
                                fontStyle: "italic", 
                                color: "#718096",
                                marginLeft: "6px",
                                fontSize: "10px"
                            }}>
                                ({weightInfo.displayText})
                            </span>
                        )}
                    </div>
                    <div style={macrosStyle}>{macrosText}</div>
                </>
            );
        }

        if (variant === "detailed") {
            return (
                <>
                    <div style={nameStyle}>
                        {food.brandName
                            ? `${food.brandName} ${food.displayName}`
                            : food.displayName}
                    </div>
                    <div style={portionStyle}>{portionText}</div>
                    <div style={macrosStyle}>{macrosText}</div>
                </>
            );
        }

        // Card variant
        return (
            <div style={cardContainerStyle}>
                <div style={infoStyle}>
                    <div style={nameStyle}>{food.displayName}</div>
                    {food.brandName && (
                        <div style={brandStyle}>{food.brandName}</div>
                    )}
                    <div style={portionStyle}>{portionText}</div>
                    <div style={macrosStyle}>{macrosText}</div>
                </div>
                {showAddButton && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAdd?.();
                        }}
                        style={addButtonStyle}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#38a169")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#48bb78")
                        }
                    >
                        Add
                    </button>
                )}
            </div>
        );
    };

    return (
        <div
            className={className}
            style={isHovered ? hoverStyle : baseStyle}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {renderContent()}
        </div>
    );
};

/**
 * Compact version for use in modals and lists
 */
export const CompactFoodSummary: React.FC<Omit<FoodSummaryProps, "variant">> = (
    props
) => <FoodSummary {...props} variant="compact" />;

/**
 * Detailed version for search results
 */
export const DetailedFoodSummary: React.FC<
    Omit<FoodSummaryProps, "variant">
> = (props) => <FoodSummary {...props} variant="detailed" />;

/**
 * Card version with optional add button
 */
export const CardFoodSummary: React.FC<Omit<FoodSummaryProps, "variant">> = (
    props
) => <FoodSummary {...props} variant="card" />;
